import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, Download, Mail, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Design tokens - Nouvelle charte graphique
const colors = {
  darkBlue: '#173444',
  cyan: '#40d0d3',
  cream: '#f9f8f2',
  yellow: '#ffc239',
  pink: '#ff8ce9',
  white: '#ffffff',
  gray: '#6b7280'
};

// Profile colors
const profileColors = {
  fantome: '#6b7280',
  brouillon: '#f59e0b',
  prometteur: '#10b981',
  irresistible: '#ef4444'
};

// Response options
const responseOptions = [
  { value: 1, label: 'Jamais' },
  { value: 2, label: 'Rarement' },
  { value: 3, label: 'Parfois' },
  { value: 4, label: 'Souvent' },
  { value: 5, label: 'Toujours' }
];

// Questions organized by blocks
const quizBlocks = [
  {
    id: 'block1',
    title: 'Résultats perçus',
    subtitle: 'Ce que ton client pense obtenir concrètement',
    questions: [
      { id: 1, text: "Quand tu décris ton offre, ton client comprend immédiatement ce qu'il va obtenir comme résultat concret." },
      { id: 2, text: "La transformation que tu promets est mesurable ou observable par ton client." },
      { id: 3, text: "Ton client peut expliquer ton offre à quelqu'un d'autre en 30 secondes." }
    ]
  },
  {
    id: 'block2',
    title: 'Probabilité de succès perçue',
    subtitle: 'Est-ce que ton client croit que ça va marcher pour lui ?',
    questions: [
      { id: 4, text: "Tu as des preuves concrètes que ton offre fonctionne (témoignages, cas clients, résultats)." },
      { id: 5, text: "Ton client se reconnaît dans les personnes pour qui ton offre est faite." },
      { id: 6, text: "Tu expliques clairement pourquoi ton approche fonctionne mieux que les alternatives qu'il a déjà essayées." }
    ]
  },
  {
    id: 'block3',
    title: 'Prix perçu',
    subtitle: 'Est-ce que le prix semble justifié par rapport à la valeur ?',
    questions: [
      { id: 7, text: "Quand tu annonces ton prix, tes prospects réagissent avec compréhension plutôt qu'avec résistance." },
      { id: 8, text: "Tu arrives à expliquer pourquoi ton offre vaut ce prix sans te justifier ni te défendre." }
    ]
  },
  {
    id: 'block4',
    title: 'Effort perçu',
    subtitle: "Est-ce que ton client pense que ça va lui demander beaucoup ?",
    questions: [
      { id: 9, text: "Ton client comprend clairement ce qu'il devra faire de son côté pour obtenir les résultats promis." },
      { id: 10, text: "Tu montres que ton offre simplifie sa vie plutôt qu'elle n'ajoute une charge supplémentaire." }
    ]
  },
  {
    id: 'block5',
    title: 'Risque perçu',
    subtitle: "Qu'est-ce qu'il risque à dire oui ?",
    questions: [
      { id: 11, text: "Ton prospect sait exactement ce qui se passe s'il achète et n'est pas satisfait." },
      { id: 12, text: "Tu lèves activement les objections principales avant même qu'il les formule." }
    ]
  }
];

export default function OfferQuiz() {
  // States
  const [step, setStep] = useState('optin'); // optin, quiz, result
  const [currentBlock, setCurrentBlock] = useState(0);
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Check if block is complete
  const isBlockComplete = () => {
    const block = quizBlocks[currentBlock];
    return block.questions.every(q => answers[q.id] !== undefined);
  };

  // Progress percentage
  const getProgress = () => {
    const totalQuestions = 12;
    const answeredQuestions = Object.keys(answers).length;
    return (answeredQuestions / totalQuestions) * 100;
  };

  // Start quiz
  const handleStartQuiz = async () => {
    if (!firstName.trim() || !email.trim() || !acceptedTerms) {
      toast.error('Merci de remplir tous les champs et accepter les conditions.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/offer-quiz/start`, {
        first_name: firstName.trim(),
        email: email.trim().toLowerCase(),
        accepted_terms: acceptedTerms
      });

      setSessionId(response.data.session_id);

      if (response.data.status === 'completed') {
        // Already completed, show results
        const resultRes = await axios.get(`${API}/api/offer-quiz/result/${response.data.session_id}`);
        setResult(resultRes.data);
        setStep('result');
      } else {
        // Resume or start
        if (response.data.answers) {
          setAnswers(response.data.answers);
        }
        setStep('quiz');
      }
    } catch (error) {
      console.error('Start quiz error:', error);
      toast.error("Une erreur s'est produite. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  // Answer question
  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  // Next block
  const handleNextBlock = async () => {
    if (!isBlockComplete()) {
      toast.error('Merci de répondre à toutes les questions.');
      return;
    }

    if (currentBlock < quizBlocks.length - 1) {
      setCurrentBlock(prev => prev + 1);
    } else {
      // Complete quiz
      await handleCompleteQuiz();
    }
  };

  // Previous block
  const handlePrevBlock = () => {
    if (currentBlock > 0) {
      setCurrentBlock(prev => prev - 1);
    }
  };

  // Complete quiz
  const handleCompleteQuiz = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/offer-quiz/complete`, {
        session_id: sessionId,
        answers: answers
      });

      if (response.data.status === 'completed') {
        setResult({
          first_name: firstName,
          score: response.data.score,
          profile: response.data.profile,
          title: response.data.profile.title_template.replace('{prenom}', firstName)
        });
        setStep('result');

        // Send email automatically
        try {
          await axios.post(`${API}/api/offer-quiz/send-email/${sessionId}`);
          setEmailSent(true);
        } catch (e) {
          console.error('Email send error:', e);
        }
      }
    } catch (error) {
      console.error('Complete quiz error:', error);
      toast.error("Une erreur s'est produite. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  // Download PDF
  const handleDownloadPDF = () => {
    window.open(`${API}/api/offer-quiz/download-pdf/${sessionId}`, '_blank');
  };

  // Resend email
  const handleResendEmail = async () => {
    setEmailSending(true);
    try {
      await axios.post(`${API}/api/offer-quiz/send-email/${sessionId}`);
      toast.success('Email envoyé avec succès !');
      setEmailSent(true);
    } catch (error) {
      toast.error("Erreur lors de l'envoi de l'email.");
    } finally {
      setEmailSending(false);
    }
  };

  // Render opt-in form
  const renderOptIn = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-xl mx-auto"
    >
      <div className="text-center mb-12">
        <h1 
          className="text-4xl md:text-5xl font-bold mb-4"
          style={{ color: colors.darkBlue, fontFamily: "'Bricolage Grotesque', sans-serif" }}
        >
          Offre irrésistible<br />
          <span style={{ color: colors.cyan }}>ou offre invisible ?</span>
        </h1>
        <p 
          className="text-lg"
          style={{ color: colors.darkBlue, fontFamily: "'Red Hat Display', sans-serif" }}
        >
          Évalue la puissance de ton offre en 2 minutes.<br /><br />
          Ce diagnostic s'appuie sur l'équation de valeur d'Alex Hormozi,<br />
          le framework utilisé par les entrepreneurs<br />
          qui construisent des offres à 7 et 8 chiffres.<br /><br />
          Découvre ton profil et les leviers concrets à activer<br />
          pour rendre ton offre irrésistible.
        </p>
      </div>

      <div 
        className="p-8 rounded-2xl shadow-lg"
        style={{ backgroundColor: colors.white }}
      >
        <div className="space-y-6">
          <div>
            <label 
              className="block text-sm font-medium mb-2"
              style={{ color: colors.darkBlue }}
            >
              Ton prénom
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Comment tu t'appelles ?"
              className="w-full px-4 py-3 rounded-xl border-2 focus:outline-none transition-colors"
              style={{ 
                borderColor: colors.cyan,
                fontFamily: "'Red Hat Display', sans-serif",
                color: colors.darkBlue,
                backgroundColor: colors.white
              }}
              data-testid="offer-quiz-firstname"
            />
          </div>

          <div>
            <label 
              className="block text-sm font-medium mb-2"
              style={{ color: colors.darkBlue }}
            >
              Ton email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ton@email.com"
              className="w-full px-4 py-3 rounded-xl border-2 focus:outline-none transition-colors"
              style={{ 
                borderColor: colors.cyan,
                fontFamily: "'Red Hat Display', sans-serif",
                color: colors.darkBlue,
                backgroundColor: colors.white
              }}
              data-testid="offer-quiz-email"
            />
          </div>

          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="terms"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-1 w-5 h-5 rounded"
              style={{ accentColor: colors.cyan }}
              data-testid="offer-quiz-terms"
            />
            <label 
              htmlFor="terms" 
              className="text-sm"
              style={{ color: colors.gray }}
            >
              J'accepte de recevoir mon résultat par email et des conseils pour améliorer mon offre. 
              Tu peux te désinscrire à tout moment.
            </label>
          </div>

          <button
            onClick={handleStartQuiz}
            disabled={loading || !firstName.trim() || !email.trim() || !acceptedTerms}
            className="w-full py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ 
              backgroundColor: colors.cyan,
              color: colors.darkBlue,
              fontFamily: "'Bricolage Grotesque', sans-serif"
            }}
            data-testid="offer-quiz-start-btn"
          >
            {loading ? (
              <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            ) : (
              "C'est parti ! →"
            )}
          </button>
        </div>
      </div>

      <p 
        className="text-center text-sm mt-6"
        style={{ color: colors.gray }}
      >
        🔒 Tes données sont protégées et ne seront jamais partagées.
      </p>
    </motion.div>
  );

  // Render quiz questions
  const renderQuiz = () => {
    const block = quizBlocks[currentBlock];
    
    return (
      <motion.div
        key={currentBlock}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="max-w-3xl mx-auto"
      >
        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span 
              className="text-sm font-medium"
              style={{ color: colors.darkBlue }}
            >
              Bloc {currentBlock + 1}/5
            </span>
            <span 
              className="text-sm"
              style={{ color: colors.gray }}
            >
              {Math.round(getProgress())}% complété
            </span>
          </div>
          <div 
            className="h-2 rounded-full overflow-hidden"
            style={{ backgroundColor: colors.cream }}
          >
            <motion.div
              className="h-full rounded-full"
              style={{ backgroundColor: colors.cyan }}
              initial={{ width: 0 }}
              animate={{ width: `${getProgress()}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        {/* Block header */}
        <div className="text-center mb-8">
          <h2 
            className="text-2xl md:text-3xl font-bold mb-2"
            style={{ color: colors.darkBlue, fontFamily: "'Bricolage Grotesque', sans-serif" }}
          >
            {block.title}
          </h2>
          <p style={{ color: colors.gray }}>
            {block.subtitle}
          </p>
        </div>

        {/* Questions */}
        <div className="space-y-8">
          {block.questions.map((question, idx) => (
            <motion.div
              key={question.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="p-6 rounded-2xl"
              style={{ backgroundColor: colors.white }}
            >
              <p 
                className="font-medium mb-4"
                style={{ color: colors.darkBlue, fontFamily: "'Red Hat Display', sans-serif" }}
              >
                {question.text}
              </p>
              
              <div className="flex flex-wrap gap-2">
                {responseOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleAnswer(question.id, option.value)}
                    className="px-4 py-2 rounded-xl text-sm font-medium transition-all"
                    style={{
                      backgroundColor: answers[question.id] === option.value ? colors.cyan : colors.cream,
                      color: answers[question.id] === option.value ? colors.darkBlue : colors.gray,
                      border: `2px solid ${answers[question.id] === option.value ? colors.cyan : 'transparent'}`
                    }}
                    data-testid={`answer-${question.id}-${option.value}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <button
            onClick={handlePrevBlock}
            disabled={currentBlock === 0}
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all disabled:opacity-30"
            style={{ 
              backgroundColor: colors.cream,
              color: colors.darkBlue
            }}
          >
            <ArrowLeft className="w-4 h-4" />
            Précédent
          </button>

          <button
            onClick={handleNextBlock}
            disabled={!isBlockComplete() || loading}
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all disabled:opacity-50"
            style={{ 
              backgroundColor: colors.cyan,
              color: colors.darkBlue
            }}
            data-testid="offer-quiz-next-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : currentBlock === quizBlocks.length - 1 ? (
              <>
                Voir mon résultat
                <Check className="w-4 h-4" />
              </>
            ) : (
              <>
                Suivant
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </motion.div>
    );
  };

  // Render result
  const renderResult = () => {
    if (!result) return null;

    const profileColor = profileColors[result.profile.key] || colors.cyan;

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-2xl mx-auto"
      >
        {/* Score card */}
        <div 
          className="p-8 rounded-3xl shadow-xl text-center mb-8"
          style={{ backgroundColor: colors.white }}
        >
          <p 
            className="text-sm uppercase tracking-wider mb-2"
            style={{ color: colors.gray }}
          >
            Ton score
          </p>
          <p 
            className="text-6xl font-bold mb-4"
            style={{ color: profileColor, fontFamily: "'Bricolage Grotesque', sans-serif" }}
          >
            {result.score}/60
          </p>
          
          {/* Profile badge */}
          <div 
            className="inline-flex items-center gap-3 px-6 py-3 rounded-full mb-6"
            style={{ backgroundColor: profileColor }}
          >
            <span className="text-3xl">{result.profile.emoji}</span>
            <span className="text-xl font-bold text-white">
              {result.profile.name}
            </span>
          </div>

          {/* Title */}
          <h2 
            className="text-xl md:text-2xl font-bold mb-6"
            style={{ color: colors.darkBlue, fontFamily: "'Bricolage Grotesque', sans-serif" }}
          >
            {result.title}
          </h2>

          {/* Description */}
          <div 
            className="p-6 rounded-xl text-left mb-6"
            style={{ backgroundColor: colors.cream }}
          >
            <h3 
              className="font-bold mb-2"
              style={{ color: colors.darkBlue }}
            >
              Ce que ça signifie :
            </h3>
            <p style={{ color: colors.gray, lineHeight: 1.7 }}>
              {result.profile.description}
            </p>
          </div>

          {/* Need */}
          <div 
            className="p-6 rounded-xl text-left mb-6"
            style={{ backgroundColor: colors.cream }}
          >
            <h3 
              className="font-bold mb-2"
              style={{ color: colors.darkBlue }}
            >
              Ton besoin prioritaire :
            </h3>
            <p style={{ color: colors.gray, lineHeight: 1.7 }}>
              {result.profile.need}
            </p>
          </div>

          {/* Solution */}
          <div 
            className="p-6 rounded-xl text-left"
            style={{ 
              backgroundColor: colors.cyan + '20',
              borderLeft: `4px solid ${colors.cyan}`
            }}
          >
            <h3 
              className="font-bold mb-2"
              style={{ color: colors.darkBlue }}
            >
              Comment Boost-StratégIA peut t'aider :
            </h3>
            <p style={{ color: colors.darkBlue, lineHeight: 1.7 }}>
              {result.profile.solution}
            </p>
          </div>
        </div>

        {/* CTA - AVANT le PDF */}
        <div 
          className="p-8 rounded-2xl text-center mb-8"
          style={{ 
            background: `linear-gradient(135deg, ${colors.darkBlue} 0%, ${colors.cyan} 100%)`
          }}
        >
          <p className="text-white text-lg mb-4">
            Ton score révèle où ton offre en est aujourd'hui.
          </p>
          <p className="text-white font-bold text-xl mb-6">
            Boost-StratégIA te montre exactement comment la rendre irrésistible, étape par étape.
          </p>
          <a
            href="/pricing"
            className="inline-block px-8 py-4 rounded-xl font-bold text-lg transition-all hover:scale-[1.02]"
            style={{ 
              backgroundColor: colors.cyan,
              color: colors.darkBlue
            }}
            data-testid="offer-quiz-cta"
          >
            Je teste GRATUITEMENT pendant 7 jours Boost-StratégIA →
          </a>
        </div>

        {/* Actions - PDF et Email */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <button
            onClick={handleDownloadPDF}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-bold transition-all hover:scale-[1.02]"
            style={{ 
              backgroundColor: colors.yellow,
              color: colors.darkBlue
            }}
            data-testid="offer-quiz-download-pdf"
          >
            <Download className="w-5 h-5" />
            Télécharger mon rapport PDF
          </button>

          {!emailSent && (
            <button
              onClick={handleResendEmail}
              disabled={emailSending}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-bold transition-all"
              style={{ 
                backgroundColor: colors.cream,
                color: colors.darkBlue
              }}
            >
              {emailSending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Mail className="w-5 h-5" />
                  Recevoir par email
                </>
              )}
            </button>
          )}
        </div>

        {emailSent && (
          <p 
            className="text-center text-sm mb-8"
            style={{ color: colors.gray }}
          >
            ✅ Ton résultat a été envoyé à ton adresse email !
          </p>
        )}
      </motion.div>
    );
  };

  return (
    <div 
      className="min-h-screen py-12 px-4"
      style={{ 
        backgroundColor: colors.cream,
        fontFamily: "'Red Hat Display', sans-serif"
      }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <a href="/" className="inline-block">
            <span 
              className="text-2xl font-bold"
              style={{ color: colors.darkBlue, fontFamily: "'Bricolage Grotesque', sans-serif" }}
            >
              BOOST-STRATEGIA
            </span>
          </a>
        </div>

        <AnimatePresence mode="wait">
          {step === 'optin' && renderOptIn()}
          {step === 'quiz' && renderQuiz()}
          {step === 'result' && renderResult()}
        </AnimatePresence>
      </div>
    </div>
  );
}
